import { tick } from 'svelte';

/**
 * Svelte action that converts CKEditor media embeds (`<oembed url="...">`) into
 * playable iframes. Legacy MVC pages rely on iframely.load.js; Svelte SPAs use this.
 */
export function initCkEditorEmbeds(node: HTMLElement) {
    async function processEmbeds() {
        await tick();

        const oembeds = node.querySelectorAll('oembed[url]');
        oembeds.forEach((oembed) => {
            const url = oembed.getAttribute('url');
            if (!url) return;

            const figure = oembed.closest('figure');
            const container = figure || oembed.parentElement;
            if (!container) return;

            const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
            if (youtubeMatch) {
                const videoId = youtubeMatch[1];
                container.innerHTML = `
                    <div class="relative w-full" style="padding-bottom: 56.25%;">
                        <iframe
                            class="absolute inset-0 w-full h-full rounded-lg"
                            src="https://www.youtube.com/embed/${videoId}"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                    </div>
                `;
                return;
            }

            const twitterMatch = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
            if (twitterMatch) {
                const tweetId = twitterMatch[1];
                container.innerHTML = `<blockquote class="twitter-tweet" data-tweet-id="${tweetId}"><a href="${url}">Loading tweet...</a></blockquote>`;
                loadTwitterWidgets(container);
                return;
            }

            const vimeoMatch = url.match(/vimeo\.com\/(\d+)(\/\w+)?/);
            if (vimeoMatch) {
                let videoId = vimeoMatch[1];
                // unlisted privacy hash
                // reformat /videoId/hash to /videoId?h=hash for embed
                if (vimeoMatch.length > 2 && vimeoMatch[2]){
                    videoId += `?h=${vimeoMatch[2].substring(1)}`;
                }
                container.innerHTML = `
                    <div class="relative w-full" style="padding-bottom: 56.25%;">
                        <iframe
                            class="absolute inset-0 w-full h-full rounded-lg"
                            src="https://player.vimeo.com/video/${videoId}"
                            frameborder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowfullscreen
                        ></iframe>
                    </div>
                `;
                return;
            }

            container.innerHTML = `<a href="${url}" target="_blank" rel="noopener" class="text-teal-600 hover:underline">${url}</a>`;
        });

        if (node.querySelector('.twitter-tweet:not([data-processed])')) {
            loadTwitterWidgets(node);
        }
    }

    processEmbeds();
    return { update: processEmbeds };
}

function loadTwitterWidgets(container: Element): void {
    type TwitterWindow = Window & { twttr?: { widgets?: { load: (el: Element) => void } } };
    const win = window as TwitterWindow;
    if (!win.twttr) {
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.onload = () => win.twttr?.widgets?.load(container);
        document.body.appendChild(script);
    } else {
        win.twttr.widgets?.load(container);
    }
}
